<template>
  <div v-if="loading">
    <spinner />
  </div>
  <div v-else class="container">
    <membership-plan-list
      v-if="activeStep === 1"
      :initial-values="membershipPlans"
      @createNewPlan="activeStep = 2"
      @editPlan="handleEdit($event)"
      @delete="handleDelete"
    />
    <div v-if="activeStep === 1">
      <h1 class="subs-title" style="margin-top: 2.5rem">{{ adminText('organizationAll.manage_cover_for_all_membership_plans_5447a4b5', 'Manage cover for all Membership Plans') }}</h1>
      <p class="page-description">
        {{ adminText('organizationAll.here_you_can_add_or_edit_a_universal_cover_th_daeb516e', 'Here you can add or edit a universal cover that will apply to all membership plans.') }}
      </p>
      <cover-field v-model="coverUrl" :disable-delete="true" />
    </div>
    <create-or-edit-membership
      v-if="activeStep === 2"
      :initial-value="initialValue"
      exclude-order-item-templates
      :include-cover-image-field="!hasOrganizationMembershipCoverImage()"
      @cancel="handleReset"
      @create="handleCreate"
      @update="handleUpdate"
    />
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="organizationText(snackbar.message)"
      :color="snackbar.color"
    />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import MembershipPlanList from '@/components/organizations/memberships/MembershipPlanList.vue'
import CreateOrEditMembership from '@/components/organizations/memberships/CreateOrEditMembership.vue'
import {
  CREATE_MEMBERSHIP_PLAN,
  DELETE_MEMBERSHIP_PLAN,
  GET_SPORT_ORGANIZATION_MEMBERSHIP_PLANS,
  UPDATE_MEMBERSHIP_PLAN,
  UPDATE_SPORT_ORGANIZATION_MEMBERSHIP_PLAN,
} from '@/graphql/membershipPlan.graphql'
import Spinner from './../../shared/spinner.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import { omit } from 'lodash'
import { UPDATE_IMAGE_URLS } from '@/graphql/organization.graphql'

export default {
  mixins: [OrganizationLocalization],
  name: 'sport-memberships-container',
  components: {
    MembershipPlanList,
    CreateOrEditMembership,
    SimpleSnackbar,
    Spinner,
  },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      activeStep: 1,
      membershipPlans: [],
      loading: true,
      initialValue: {},
      snackbar: {
        open: false,
        message: '',
        color: 'primary',
      },
      organization: {},
      coverUrl: null,
    }
  },
  watch: {
    coverUrl: {
      async handler(val) {
        if (val === this.organization.imageUrls.membershipCoverUrl) {
          return
        }

        const { errors } = await this.$apollo.mutate({
          mutation: UPDATE_IMAGE_URLS,
          variables: {
            organizationSlug: this.organizationSlug,
            imageUrls: {
              ...this.organization.imageUrls,
              membershipCoverUrl: val,
            },
          },
        })
        if (errors) {
          this.snackbarMessage = 'Something went wrong. Try again.'
          this.snackbarOpen = true
        }

        await this.fetchMembershipPlans()
      },
    },
  },
  async mounted() {
    await this.fetchMembershipPlans()
  },
  methods: {
    async handleCreate(info) {
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_MEMBERSHIP_PLAN,
        variables: {
          organizationSlug: this.organizationSlug,
          orderItemTemplates: [],
          membershipPlanInput: omit(info, ['orderItemTemplates']),
        },
      })
      await this.showSnackbarMsgAndRefetchData(errors, 'Plan created')
    },
    async handleUpdate(info) {
      const { id, orderItemTemplates, __typename, ...membershipPlanInput } = info
      const { errors } = await this.$apollo.mutate({
        mutation: UPDATE_SPORT_ORGANIZATION_MEMBERSHIP_PLAN,
        variables: {
          organizationSlug: this.organizationSlug,
          membershipPlanId: id,
          membershipPlanInput,
        },
      })
      await this.showSnackbarMsgAndRefetchData(errors, 'Plan updated')
    },
    async handleDelete(selected) {
      const { errors } = await this.$apollo.mutate({
        mutation: DELETE_MEMBERSHIP_PLAN,
        variables: {
          organizationSlug: this.organizationSlug,
          membershipPlanId: selected.map(s => parseInt(s)),
        },
      })
      this.activeStep = 0
      await this.showSnackbarMsgAndRefetchData(errors, 'Plan deleted')
    },
    async handleRefetchData() {
      await this.fetchMembershipPlans()
      this.handleReset()
    },
    async showSnackbarMsgAndRefetchData(errors, msg) {
      if (errors) {
        this.snackbar = {
          open: true,
          message: 'Oops, something went wrong!',
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          message: this.organizationMessage('organizationAll.value0_successfully_4bf9e6b5', '{{value0}} successfully!', { value0: msg }),
        }
        await this.handleRefetchData()
      }
    },
    handleEdit(selectedPlanId) {
      this.initialValue = this.membershipPlans.find(plan => plan.id === selectedPlanId)
      this.activeStep = 2
    },
    handleReset() {
      this.activeStep = 1
      this.initialValue = {}
    },
    async fetchMembershipPlans() {
      const { data, errors } = await this.$apollo.query({
        query: GET_SPORT_ORGANIZATION_MEMBERSHIP_PLANS,
        variables: {
          organizationSlug: this.organizationSlug,
        },
      })
      if (errors) {
        await this.showSnackbarMsgAndRefetchData(errors)
      } else {
        this.membershipPlans = data.getOrganizationMembershipPlans
        this.organization = data.getOrganizationBySlug
        if (this.organization.imageUrls && this.organization.imageUrls.membershipCoverUrl) {
          this.coverUrl = this.organization.imageUrls.membershipCoverUrl
        }

        this.loading = false
      }
    },
    hasOrganizationMembershipCoverImage() {
      return this.organization.imageUrls && this.organization.imageUrls.membershipCoverUrl
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0 !important;
  margin: 0 !important;
  display: flex;
  flex-direction: column;
  max-width: 43.75rem;
}

.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  color: #979797;
}

.subs-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 1.5rem;
}
</style>
